/**
 * CallRecorderModal Component
 *
 * Compact consent modal for starting call recordings.
 * After consent, the FloatingRecorderWidget takes over for the actual recording.
 */

import { useState, useEffect } from 'react'
import { Mic, Monitor, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCallRecordingContext } from '@/contexts/CallRecordingContext'
import type { AudioCaptureMode } from '@/contexts/CallRecordingContext'

interface CallRecorderModalProps {
  isOpen: boolean
  onClose: () => void
  ticketId?: number
  ticketSummary?: string
  onRecordingStart?: () => void
}

export function CallRecorderModal({
  isOpen,
  onClose,
  ticketId,
  ticketSummary,
  onRecordingStart,
}: CallRecorderModalProps) {
  const [captureMode, setCaptureMode] = useState<AudioCaptureMode>('microphone')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isStarting, setIsStarting] = useState(false)

  const {
    startRecording,
    isMicrophoneSupported,
    isSystemAudioSupported,
    error: contextError,
  } = useCallRecordingContext()

  useEffect(() => {
    if (!isOpen) {
      setCaptureMode('microphone')
      setErrorMessage(null)
      setIsStarting(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (contextError) {
      setErrorMessage(contextError)
      setIsStarting(false)
    }
  }, [contextError])

  const handleStartRecording = async () => {
    try {
      setErrorMessage(null)
      setIsStarting(true)
      await startRecording(captureMode, ticketId, ticketSummary)
      onRecordingStart?.()
      onClose()
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to start recording')
      setIsStarting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-xs mx-4 p-4">
        <h2 className="text-sm font-semibold mb-1">Record Call</h2>
        {ticketSummary && (
          <p className="text-xs text-gray-500 truncate mb-2">
            #{ticketId}: {ticketSummary}
          </p>
        )}
        <div className="space-y-3">
          <div className="flex gap-2 p-2 bg-blue-50 rounded-md text-xs text-blue-800">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <p>This call will be recorded and transcribed. Ensure all parties consent.</p>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium">Audio Source</label>
            <label className={`flex items-center gap-2 p-2 border rounded-md cursor-pointer text-xs ${
              captureMode === 'microphone' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
            } ${!isMicrophoneSupported ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <input type="radio" name="captureMode" value="microphone" checked={captureMode === 'microphone'} onChange={() => setCaptureMode('microphone')} disabled={!isMicrophoneSupported} className="shrink-0" />
              <Mic className="h-3.5 w-3.5 shrink-0" />
              <div><span className="font-medium">Microphone</span><span className="text-gray-500 ml-1">- Speaker mode</span></div>
            </label>
            <label className={`flex items-center gap-2 p-2 border rounded-md cursor-pointer text-xs ${
              captureMode === 'system' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
            } ${!isSystemAudioSupported ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <input type="radio" name="captureMode" value="system" checked={captureMode === 'system'} onChange={() => setCaptureMode('system')} disabled={!isSystemAudioSupported} className="shrink-0" />
              <Monitor className="h-3.5 w-3.5 shrink-0" />
              <div><span className="font-medium">System Audio</span><span className="text-gray-500 ml-1">- Softphone</span>{!isSystemAudioSupported && <span className="text-red-500 ml-1">N/A</span>}</div>
            </label>
          </div>
          {errorMessage && (
            <div className="flex gap-2 p-2 bg-red-50 rounded-md text-xs text-red-800">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <p>{errorMessage}</p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={isStarting}>Cancel</Button>
            <Button size="sm" onClick={handleStartRecording} disabled={isStarting} style={{ backgroundColor: '#2563eb', color: 'white' }} className="gap-1.5">
              <Mic className="h-3.5 w-3.5" />
              {isStarting ? 'Starting...' : 'Start Recording'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
